import React, { useEffect, useState, useRef } from 'react';
import AdminDashboardTemplate from '@/components/templates/AdminDashboardTemplate';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { PAGE } from '@/routes';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import NoSubscribeImg from '@/assets/icons/no-subscribe.png';
import SubscriberPrimaryIcon from '@/assets/icons/subscriber-primary.svg';
import NewSubscriberPrimaryIcon from '@/assets/icons/newSubscriber-primary.svg';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';
import { Button } from '@/components/ui/button';
import SubscriberList from '@/components/organisms/SubscriberList';
import UpwardTrendIcon from '@/assets/icons/trend-upward.svg';
import DownwardTrendIcon from '@/assets/icons/trend-downward.svg';
import { adminPanelService } from '@/services/admin-panel.service';
import EditSubscriptionModal from '@/components/molecules/EditSubscriptionModal';
import EditSubscriptionIcon from '@/assets/icons/edit-subscription.svg';
import SuspendUserModal from '@/components/molecules/SuspendUserModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import SelectField from '@/components/atoms/SelectField';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDeviceType } from '@/hooks/useDeviceType';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import LogoutIcon from '@/assets/icons/logout-red.svg';
import { authService } from '@/services/auth.service';

import DefaultAvatar from '@/assets/images/default-user.png';
import InfoModal from '@/components/molecules/InfoModal';
import unregisterUserIcon from '@/assets/icons/unregister-user.svg';
import { useClerk } from '@clerk/clerk-react';

const AdminDashboardPage: React.FC = () => {
  const [selectedYearlyStats, setSelectedYearlyStats] = useState<any>(new Date().getFullYear().toString());
  const [yearlyStatsData, setYearlyStatsData] = useState<any>(null);
  const [subscribers, setSubscribers] = useState<any>(null);
  const [totalSubscribers, setTotalSubscribers] = useState<any>(null);
  const [selectedSubscriber, setSelectedSubscriber] = useState<any>(null);
  const [activeModal, setActiveModal] = useState<any>(null);
  const [totalSubscribersStatsData, setTotalSubscribersStatsData] = useState<any>(null);
  const [newSubscribersStatsData, setNewSubscribersStatsData] = useState<any>(null);
  const [loadingNewSubscribersStats, setLoadingNewSubscribersStats] = useState(false);
  const [loadingTotalSubscribersStats, setLoadingTotalSubscribersStats] = useState(false);

  const currentYear = new Date().getFullYear();
  const yearsOptions: { label: string; value: string }[] = Array.from({ length: 10 }, (_, i) => ({
    label: (currentYear - 4 + i).toString(),
    value: (currentYear - 4 + i).toString()
  }));

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const navigate = useNavigate();
  const { isMobileView } = useDeviceType();
  const chartScrollRef = useRef<HTMLDivElement>(null);
  const { signOut } = useClerk();
  console.log({ currentUser });

  const calculateTrendDirection = (data: any[]): boolean => {
    if (data.length < 2) return true; // Default to upward if insufficient data

    // Filter out null values (future months) to get only actual data points
    const validData = data.filter((item) => item.value !== null);

    if (validData.length < 2) return true; // Default to upward if insufficient valid data

    // simple first vs last comparison using valid data only
    const firstValue = validData[0].value;
    const lastValue = validData[validData.length - 1].value;
    const totalChange = lastValue - firstValue;

    return totalChange >= 0;
  };

  // Use upward trend by default, change to subscriptionDataDownward for downward trend
  const chartData: any[] = yearlyStatsData || [];
  const isUpwardTrend = calculateTrendDirection(chartData);

  console.log('chartData', chartData);

  // Calculate smart Y-axis domain with minimum threshold of 100
  const getYAxisDomain = () => {
    const maxValue = Math.max(...chartData.map((d) => d.value || 0));
    const minThreshold = 100;

    if (maxValue <= minThreshold) {
      return { domain: [0, minThreshold], ticks: [0, 20, 40, 60, 80, 100] };
    }

    // Round up to next multiple of 50 for values above 100
    const roundedMax = Math.ceil((maxValue + 20) / 50) * 50;
    const tickInterval = roundedMax / 5;
    const ticks = Array.from({ length: 6 }, (_, i) => i * tickInterval);

    return { domain: [0, roundedMax], ticks };
  };

  const yAxisConfig = getYAxisDomain();

  const handleChangeSubscription = (id: string) => {
    console.log('handleChangeSubscription', id);
    setActiveModal('handleChangeSubscription');
    setSelectedSubscriber(id);
  };

  const handleSuspend = (id: string) => {
    console.log('handleSuspend', id);
    setActiveModal('suspendUser');
    setSelectedSubscriber(id);
  };

  useEffect(() => {
    // Set breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Dashboard',
          path: PAGE.ADMIN_DASHBOARD.path
        }
      ])
    );
  }, [dispatch]);

  const fetchSubscribers = async () => {
    try {
      const res = await adminPanelService.getSubscribers({
        limit: 4
      });
      setSubscribers(res?.data?.users || []);
      setTotalSubscribers(res?.data?.count || 0);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchYearlyStats = async () => {
    try {
      const { data: yearlyStatsData } = await adminPanelService.getYearlyStats(selectedYearlyStats);

      // Format the data to match chart structure
      const formattedData = formatYearlyStatsData(yearlyStatsData);
      console.log('formattedData', formattedData);
      setYearlyStatsData(formattedData);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTotalSubscribersStats = async () => {
    setLoadingTotalSubscribersStats(true);
    try {
      const { data: totalSubscribersStatsData } = await adminPanelService.getTotalSubscribersStats();

      setTotalSubscribersStatsData(totalSubscribersStatsData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingTotalSubscribersStats(false);
    }
  };

  const fetchNewSubscribersStats = async () => {
    setLoadingNewSubscribersStats(true);
    try {
      const { data: newSubscribersStatsData } = await adminPanelService.getNewSubscribersStats();

      setNewSubscribersStatsData(newSubscribersStatsData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingNewSubscribersStats(false);
    }
  };

  // Function to format yearly stats data for chart
  const formatYearlyStatsData = (data: any[]): any[] => {
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // getMonth() returns 0-based, so add 1

    // Create a map of existing data by month
    const dataMap = new Map();
    data?.forEach((item: any) => {
      dataMap.set(item.month, item.count);
    });

    // Generate all 12 months
    const formattedData = monthNames.map((monthName, index) => {
      const monthNumber = index + 1;
      const isCurrentYear = parseInt(selectedYearlyStats) === currentYear;
      const isFutureMonth = isCurrentYear && monthNumber > currentMonth;

      return {
        month: monthName,
        value: isFutureMonth ? null : dataMap.get(monthNumber) || 0 // null for future months, 0 for months with no data
      };
    });

    return formattedData;
  };

  // Function to auto-scroll to current month
  const scrollToCurrentMonth = () => {
    if (!isMobileView || !chartScrollRef.current || !chartData.length) return;

    const currentMonth = new Date().getMonth(); // 0-based index
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const currentMonthName = monthNames[currentMonth];

    // Find the index of current month in chart data
    const currentMonthIndex = chartData.findIndex((item: any) => item.month === currentMonthName);

    if (currentMonthIndex !== -1) {
      // Calculate scroll position - each month takes approximately 70px width
      const scrollPosition = Math.max(0, currentMonthIndex * 70 - chartScrollRef.current.clientWidth / 2 + 35);

      // Smooth scroll to position
      chartScrollRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    fetchSubscribers();
    fetchTotalSubscribersStats();
    fetchNewSubscribersStats();
  }, []);

  useEffect(() => {
    fetchYearlyStats();
  }, [selectedYearlyStats]);

  // Auto-scroll to current month when chart data changes (with slight delay to ensure rendering)
  useEffect(() => {
    if (chartData.length > 0 && isMobileView) {
      const timer = setTimeout(() => {
        scrollToCurrentMonth();
      }, 100); // Small delay to ensure chart is rendered

      return () => clearTimeout(timer);
    }
  }, [chartData, isMobileView]);

  const displayBanUntilToast = (banUserResponseData: any) => {
    if (banUserResponseData?.banUntil) {
      const bannedUntilDate = new Date(banUserResponseData?.banUntil);
      const formattedDate = bannedUntilDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      toast.success('User banned until ' + formattedDate);
    } else {
      toast.success('User banned successfully');
    }
  };

  const handleBanUser = async (banPeriod: number, timeUnit: string) => {
    console.log({ banPeriod, timeUnit, selectedSubscriber });
    try {
      const { data: banUserResponseData } = await adminPanelService.banUser(selectedSubscriber, {
        ban_period: banPeriod,
        time_unit: timeUnit
      });

      displayBanUntilToast(banUserResponseData);

      setSelectedSubscriber(null);
      setActiveModal(null);
      fetchSubscribers();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubscriberClick = (id: string) => {
    console.log('handleSubscriberClick', id);
    navigate(`${PAGE.ADMIN_SUBSCRIBERS.path}/${id}`);
  };

  const handleLogout = async () => {
    try {
      await authService.logout(signOut);
      navigate('/login');
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Logout failed. Please try again');
    }
  };

  return (
    <AdminDashboardTemplate className="flex flex-col !p-0 md:!p-6">
      <MobileAppBar title="ADMIN PANEL" variant="primary" rightIcon={LogoutIcon} onRightIconClick={handleLogout} />

      <div className="p-4 md:p-0 bg-[#FBFBFF] md:bg-transparent">
        <div className="flex items-center gap-4 mb-5 md:mb-0">
          <div className="rounded-full bg-lightPurple-200 flex items-center justify-center md:hidden">
            <img className="w-[60px] h-[60px]" src={DefaultAvatar} alt="avatar" />
          </div>
          <h1 className="text-base md:text-lg text-gray-900 leading-[100%] md:mb-6">
            Hello Admin,{' '}
            <span className="text-xl md:text-lg font-bold text-primary block md:inline">
              {currentUser?.name && currentUser?.name !== 'undefined ' ? currentUser?.name?.toUpperCase() : 'MARCO'}
            </span>
          </h1>
        </div>

        <div className="h-full flex-1 flex flex-col space-y-6 md:space-y-8">
          {/* Chart */}
          <div className="md:h-1/2 flex flex-col bg-white rounded-[16px] px-4 pt-3 pb-6 border border-[#E9ECEF] space-y-4">
            <header className="flex items-center justify-between gap-4">
              <p className="font-bold">Subscription</p>

              <SelectField
                name="year"
                options={yearsOptions}
                value={selectedYearlyStats}
                onChange={(value) => {
                  setSelectedYearlyStats(value);
                }}
                variant="text"
                buttonClassName="!font-bold !text-secondary"
              />
            </header>
            <div className="flex-1 flex relative md:min-h-[300px]">
              {chartData.length > 0 ? (
                <div
                  ref={chartScrollRef}
                  className={`w-full ${isMobileView ? 'overflow-x-scroll scrollbar-always-visible' : ''}`}
                >
                  <ResponsiveContainer
                    width={isMobileView ? Math.max(chartData.length * 70, 350) : '100%'}
                    height={isMobileView ? 200 : '100%'}
                    minWidth={isMobileView ? 350 : undefined}
                  >
                    <AreaChart
                      data={chartData}
                      margin={{
                        top: 20,
                        right: isMobileView ? 20 : 0,
                        left: isMobileView ? 20 : 15,
                        bottom: 5
                      }}
                    >
                      <defs>
                        {/* Green gradient for upward trend */}
                        <linearGradient id="greenGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="40%" stopColor="#8EEB63" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#8EEB63" stopOpacity={0} />
                        </linearGradient>
                        {/* Red gradient for downward trend */}
                        <linearGradient id="redGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="40%" stopColor="#E8507E" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#E8507E" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      {/* Horizontal grid lines */}
                      <CartesianGrid
                        strokeDasharray="0 0"
                        horizontal={true}
                        vertical={false}
                        stroke="#E5E7EB"
                        strokeWidth={1}
                      />
                      {/* Vertical dashed lines under the filled area */}
                      <CartesianGrid
                        strokeDasharray="4 4"
                        horizontal={false}
                        vertical={true}
                        stroke={isUpwardTrend ? '#8EEB63' : '#E8507E'}
                        strokeWidth={1}
                        opacity={0.4}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#93999F', dy: 10 }}
                        className="text-xs font-bold text-secondary"
                      />
                      {!isMobileView && (
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: '#93999F', dx: -15 }}
                          domain={yAxisConfig.domain}
                          ticks={yAxisConfig.ticks}
                          tickFormatter={(value) => value.toString()}
                          className="text-xs font-bold"
                          width={40}
                        />
                      )}
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
                                <p className="text-sm font-medium text-gray-600">{label}</p>
                                <p className="text-lg font-bold text-gray-900">{payload[0].value?.toLocaleString()}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                        cursor={{
                          stroke: isUpwardTrend ? '#8EEB63' : '#E8507E',
                          strokeWidth: 1,
                          strokeDasharray: '4 4'
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke={isUpwardTrend ? '#48C80C' : '#E8507E'}
                        strokeWidth={2}
                        fill={isUpwardTrend ? 'url(#greenGradient)' : 'url(#redGradient)'}
                        fillOpacity={1}
                        connectNulls={false}
                        dot={{
                          fill: isUpwardTrend ? '#48C80C' : '#E8507E',
                          strokeWidth: 2,
                          r: 4,
                          stroke: '#fff'
                        }}
                        activeDot={{
                          r: 6,
                          fill: isUpwardTrend ? '#48C80C' : '#E8507E',
                          stroke: '#fff',
                          strokeWidth: 2
                        }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center py-6 md:py-0">
                  <img src={NoSubscribeImg} alt="no subscribe" />
                  <p className="text-sm font-medium mt-3">There are no Subscription data to show</p>
                  <p className="text-xs text-secondary font-medium">
                    The data will be shown after user register to BatJet
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Total Subscribers & New Subscriber */}
          <section className="h-1/2 flex flex-col md:flex-row pb-0 gap-6 md:max-h-[324px]">
            <div className="flex flex-row md:flex-col md:justify-center gap-3 md:gap-6">
              {/* Total Subscribers */}
              <div className="w-full md:w-[304px] bg-white rounded-[16px] p-3 border border-[#E9ECEF] space-y-[6px]">
                <span className="w-9 h-9 bg-lightPurple-200 rounded-[4px] flex items-center justify-center">
                  <img src={SubscriberPrimaryIcon} alt="no subscribe" />
                </span>
                <p className="font-medium">Total Subscriber</p>
                {loadingTotalSubscribersStats ? (
                  <div className="flex items-center gap-2 !mt-1">
                    <p className="text-lg font-bold ">...</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 !mt-1">
                    <p className="text-lg font-bold ">{totalSubscribersStatsData?.allTimeCount || 0}</p>
                    {totalSubscribersStatsData?.trend === 'increase' ? (
                      <div className="flex items-center gap-1">
                        <img src={UpwardTrendIcon} alt="upward trend" />
                        <p className="text-[11px] text-[#48C80C] font-medium leading-0">
                          {totalSubscribersStatsData?.percentChange}%
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <img src={DownwardTrendIcon} alt="upward trend" />
                        <p className="text-[11px] text-[#E8507E] font-medium leading-0">
                          {totalSubscribersStatsData?.percentChange}%
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {loadingTotalSubscribersStats ? (
                  <p className="text-xs text-secondary">...</p>
                ) : (
                  <p className="text-xs text-secondary">
                    {totalSubscribersStatsData?.trend === 'increase'
                      ? `+${totalSubscribersStatsData?.difference || 0} From last year`
                      : `-${totalSubscribersStatsData?.difference || 0} From last year`}
                  </p>
                )}
              </div>

              {/* New Subscriber */}
              <div className="w-full md:w-[304px] bg-white rounded-[16px] p-3 border border-[#E9ECEF] space-y-[6px]">
                <span className="w-9 h-9 bg-lightPurple-200 rounded-[4px] flex items-center justify-center">
                  <img src={NewSubscriberPrimaryIcon} alt="no subscribe" />
                </span>
                <p className="font-medium">New Subscriber</p>
                {loadingNewSubscribersStats ? (
                  <div className="flex items-center gap-2 !mt-1">
                    <p className="text-lg font-bold ">...</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 !mt-1">
                    <p className="text-lg font-bold ">{newSubscribersStatsData?.thisYearTotal || 0}</p>
                    {newSubscribersStatsData?.trend === 'increase' ? (
                      <div className="flex items-center gap-1">
                        <img src={UpwardTrendIcon} alt="upward trend" />
                        <p className="text-[11px] text-[#48C80C] font-medium leading-0">
                          {newSubscribersStatsData?.percentChange.toFixed(2)}%
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <img src={DownwardTrendIcon} alt="upward trend" />
                        <p className="text-[11px] text-[#E8507E] font-medium leading-0">
                          {newSubscribersStatsData?.percentChange.toFixed(2)}%
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {loadingNewSubscribersStats ? (
                  <p className="text-xs text-secondary">...</p>
                ) : (
                  <p className="text-xs text-secondary">
                    {newSubscribersStatsData?.trend === 'increase'
                      ? `+${newSubscribersStatsData?.differenceFromLastYear || 0} From last year`
                      : `${newSubscribersStatsData?.differenceFromLastYear || 0} From last year`}
                  </p>
                )}
              </div>
            </div>

            {/* Subscribers */}
            <div className="flex-1 flex flex-col md:bg-white rounded-[16px] md:px-4 md:pt-3 pb-6 md:border border-[#E9ECEF] space-y-4">
              <header className="flex items-center justify-between gap-4">
                <p className="font-bold">Subscribers</p>
                <Button
                  variant="text"
                  size="sm"
                  className="text-xs md:text-lg font-semibold"
                  onClick={() => {
                    navigate(PAGE.ADMIN_SUBSCRIBERS.path);
                  }}
                >
                  View All
                </Button>
              </header>
              <div className="flex-1 flex justify-center">
                {subscribers?.length > 0 ? (
                  <SubscriberList
                    items={subscribers}
                    onChangeSubscription={handleChangeSubscription}
                    onSuspend={handleSuspend}
                    onItemClick={handleSubscriberClick}
                  />
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center py-6 md:py-0">
                    <img src={NoSubscribeImg} alt="no subscribe" />
                    <p className="text-sm font-medium mt-3">There are no Subscription data to show</p>
                    <p className="text-xs text-secondary font-medium">
                      The data will be shown after user register to BatJet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        {activeModal === 'handleChangeSubscription' && (
          <EditSubscriptionModal
            isOpen={!!selectedSubscriber}
            onClose={() => {
              setSelectedSubscriber(null);
              setActiveModal(null);
            }}
            onButtonClick={() => {}}
          />
        )}

        {activeModal === 'suspendUser' && (
          <SuspendUserModal
            isOpen={!!selectedSubscriber}
            onClose={() => {
              setSelectedSubscriber(null);
              setActiveModal(null);
            }}
            onBanClick={handleBanUser}
          />
        )}
        {/* {showSuccessSuspendUser && isMobileView && (
        <InfoModal
          isOpen={showSuccessSuspendUser}
          variant="success"
          title="User Suspended"
          message="User has been suspended successfully"
          buttonText="Go to Onboarding"
          onButtonClick={() => {setShowSuccessSuspendUser(false);}}
        />
      )} */}
      </div>
    </AdminDashboardTemplate>
  );
};

export default AdminDashboardPage;
